import React, { useContext } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { IconSize } from "shared/types/Icon"
import { Button } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TabBarScrollContext } from "./TabBarScrollGroup"

export interface TabBarArrowLeftProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

export const TabBarArrowLeft = React.memo(
  React.forwardRef<HTMLButtonElement, TabBarArrowLeftProps>(
    ({ className, ...rest }, ref) => {
      const { t } = useTranslation()
      const { canScrollLeft, scrollLeft, hasOverflow, arrowLeftRef } =
        useContext(TabBarScrollContext)

      const mergedRef = useMergedRefs(ref, arrowLeftRef)

      const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        rest.onClick?.(e)
        scrollLeft()
      }

      // Don't render if no overflow
      if (!hasOverflow) {
        return null
      }

      return (
        <Button
          ref={mergedRef}
          variant="tertiary"
          onClick={handleClick}
          disabled={!canScrollLeft}
          {...rest}
          aria-hidden
          tabIndex={-1}
          aria-label={rest["aria-label"] || t("General.Previous")}
          className={classNames(
            "btn--icon-only",
            "tab-bar__arrow tab-bar__arrow--left",
            className
          )}
        >
          <Icon name={IconName.SMALL_CARET_LEFT} size={IconSize.XS} />
        </Button>
      )
    }
  )
)

TabBarArrowLeft.displayName = "TabBar.ArrowLeft"
